import { RESOURCES, USAGE_TYPES } from '../../data/windsor'
import type {
  BookingFile,
  BookingFileStatus,
  ContractStatus,
  Payment,
  PaymentStatus,
  Resource,
  UsageType,
} from '../../types/loisirs'

export const findResource = (id: string): Resource | undefined =>
  RESOURCES.find((r) => r.id === id)

export const findUsageType = (id: string): UsageType | undefined =>
  USAGE_TYPES.find((u) => u.id === id)

/** Resource label including its parent building, e.g. "J.-A.-Lemay › Glace". */
export function resourcePath(id: string, lang: 'fr' | 'en'): string {
  const resource = findResource(id)
  if (!resource) return '—'
  const parent = resource.parentId ? findResource(resource.parentId) : undefined
  return parent
    ? `${parent.name[lang]} › ${resource.name[lang]}`
    : resource.name[lang]
}

/** Pastel fill + dark text derived from the usage type's hex. */
export function usageTint(color: string) {
  return {
    backgroundColor: `${color}14`,
    borderColor: `${color}59`,
    color,
  }
}

/** A booking that occupies the whole day, like overnight ice resurfacing. */
export const isAllDay = (start: string, end: string) =>
  start === '00:00' && (end === '23:59' || end === '24:00')

export const toDecimal = (time: string) => {
  const [h, m] = time.split(':').map(Number)
  return h + m / 60
}

/** "18:30" → "18 h 30" in French, "6:30 p.m." in English. */
export function formatTime(time: string, fr: boolean) {
  const [h, m] = time.split(':').map(Number)
  if (fr) return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')}`
  const suffix = h >= 12 ? 'p.m.' : 'a.m.'
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return m === 0
    ? `${hour12} ${suffix}`
    : `${hour12}:${String(m).padStart(2, '0')} ${suffix}`
}

/** Compact "8 h" / "8a" label used inside dense calendar cells. */
export function compactTime(time: string, fr: boolean) {
  const [h, m] = time.split(':').map(Number)
  if (fr) return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')}`
  const suffix = h >= 12 ? 'p' : 'a'
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return m === 0 ? `${hour12}${suffix}` : `${hour12}:${m}${suffix}`
}

export function bookingStatusMeta(status: BookingFileStatus) {
  const map: Record<
    BookingFileStatus,
    { fr: string; en: string; className: string }
  > = {
    brouillon: {
      fr: 'Brouillon',
      en: 'Draft',
      className: 'bg-slate-100 text-slate-600 border-slate-200',
    },
    active: {
      fr: 'Active',
      en: 'Active',
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    },
    annulee: {
      fr: 'Annulée',
      en: 'Cancelled',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
    facturee: {
      fr: 'Facturée',
      en: 'Invoiced',
      className: 'bg-indigo-50 text-[#4F46E5] border-indigo-200',
    },
  }
  return map[status]
}

export function paymentMeta(status: PaymentStatus) {
  const map: Record<
    PaymentStatus,
    { fr: string; en: string; className: string }
  > = {
    paye: {
      fr: 'Payé',
      en: 'Paid',
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    },
    virement_attendu: {
      fr: 'Virement attendu',
      en: 'Transfer pending',
      className: 'bg-amber-50 text-amber-700 border-amber-200',
    },
    impaye: {
      fr: 'Impayé',
      en: 'Unpaid',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
    rembourse: {
      fr: 'Remboursé',
      en: 'Refunded',
      className: 'bg-slate-100 text-slate-600 border-slate-200',
    },
  }
  return map[status]
}

export function paymentMethodLabel(method: Payment['method']) {
  const map: Record<Payment['method'], { fr: string; en: string }> = {
    credit: { fr: 'Carte de crédit', en: 'Credit card' },
    interac: { fr: 'Virement Interac', en: 'Interac transfer' },
    comptoir: { fr: 'Comptoir', en: 'Front counter' },
    exempt: { fr: 'Exempté', en: 'Exempt' },
  }
  return map[method]
}

export function contractStatusMeta(status: ContractStatus) {
  const map: Record<
    ContractStatus,
    { fr: string; en: string; className: string }
  > = {
    a_generer: {
      fr: 'À générer',
      en: 'To generate',
      className: 'bg-slate-100 text-slate-600 border-slate-200',
    },
    envoye: {
      fr: 'Envoyé — en attente de signature',
      en: 'Sent — awaiting signature',
      className: 'bg-amber-50 text-amber-700 border-amber-200',
    },
    signe: {
      fr: 'Signé',
      en: 'Signed',
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    },
    en_retard: {
      fr: 'En retard',
      en: 'Overdue',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
  }
  return map[status]
}

/** Flattens every booking into one entry per occurrence, for the calendar. */
export interface CalendarEvent {
  key: string
  booking: BookingFile
  date: string
  startTime: string
  endTime: string
  allDay: boolean
}

export function toCalendarEvents(bookings: BookingFile[]): CalendarEvent[] {
  return bookings
    .filter((b) => b.status !== 'annulee')
    .flatMap((booking) =>
      booking.slots.map((slot) => ({
        key: `${booking.id}-${slot.id}`,
        booking,
        date: slot.date,
        startTime: slot.startTime,
        endTime: slot.endTime,
        allDay: isAllDay(slot.startTime, slot.endTime),
      })),
    )
}

/** Sunday-first month grid covering the whole weeks around `month`. */
export function monthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1)
  const start = new Date(first)
  start.setDate(first.getDate() - first.getDay())
  return Array.from({ length: 42 }, (_, i) => {
    const day = new Date(start)
    day.setDate(start.getDate() + i)
    return day
  })
}

export const isoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`
